import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type OpeningBalanceResource,
  OpeningBalancesController_current_v1,
  OpeningBalancesController_record_v1,
  type OpeningBalancesController_record_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconArrowRight, IconCircleCheck } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { isNotFound, messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { priceInRupeesSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatPaise, paiseFromRupees } from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { Screen, ScreenFact, ScreenNote } from '../back-office/Screen';
import { DayField } from '../billing-machines/machine-fields';
import { paths } from '../paths';
import { currentBusinessDate } from '../reporting/reporting-period';
import { formatBusinessDate } from '../tickets/business-date';

/**
 * The park's opening balance (#276): `POST /v1/opening-balances`, owner only.
 *
 * **One anchor for the whole park**, not one per outlet — the book is one page
 * per day for the park, and its first page opens with one figure. Every later
 * day opens with the previous day's closing, back to this; there is no sheet
 * before it.
 *
 * **It is never edited.** A second one is a correction naming the current one
 * in `correctsOpeningBalanceId` — which may move the day as well as the amount.
 * The API refuses anything else, and both refusals carry the one that stands in
 * `details.currentOpeningBalanceId`:
 *
 * - `REPORTING_OPENING_BALANCE_ALREADY_RECORDED` — a first one when the park
 *   has one;
 * - `REPORTING_OPENING_BALANCE_SUPERSEDED` — a correction of one somebody else
 *   corrected first.
 *
 * Either becomes an offer to correct *that* one with what was typed — nobody
 * retypes anything. The same shape as `DeclareUpiDialog`, deliberately.
 */

/** `maximum: 1000000000` on `RecordOpeningBalanceDto.amountPaise`, published. */
const MAX_PAISE = 1_000_000_000;

const DAY_REQUIRED = 'Choose the day the sheet starts on.';
const AMOUNT_TOO_LARGE = `An opening balance is at most ${formatPaise(MAX_PAISE)}. Check the figure.`;

const openingSchema = z.object({
  businessDate: z.string().min(1, DAY_REQUIRED),
  // Zero is real — an empty drawer — so the price fragment, which allows it,
  // with the route's own ceiling on top.
  amount: priceInRupeesSchema.refine(
    (rupees) => paiseFromRupees(rupees) <= MAX_PAISE,
    AMOUNT_TOO_LARGE,
  ),
});

type OpeningValues = z.infer<typeof openingSchema>;

type CurrentState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'none' }
  | { readonly kind: 'found'; readonly balance: OpeningBalanceResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** A refusal naming the balance that stands, turned into an offer to correct it. */
interface CorrectionOffer {
  readonly message: string;
  /** `details.currentOpeningBalanceId`: what the correction will name. */
  readonly currentId: string;
  /** What was typed when refused — the correction sends exactly this. */
  readonly values: OpeningValues;
}

/** One line, so it reads — and is found — as one phrase. */
const standing = (balance: OpeningBalanceResource) =>
  `${formatPaise(balance.amountPaise)} at the start of ${formatBusinessDate(balance.businessDate)}`;

export function OpeningBalance() {
  const client = useApiClient();
  const today = currentBusinessDate(systemClock);

  const [current, setCurrent] = useState<CurrentState>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  const [recorded, setRecorded] = useState<OpeningBalanceResource | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [offer, setOffer] = useState<CorrectionOffer | null>(null);

  // Per body — see `lib/write-attempt.ts`. A correction after a refusal is a
  // different body, so it gets a fresh key; a retry of the same one replays.
  const attempt = useRef<WriteAttempt | null>(null);

  const form = useForm({
    resolver: zodResolver(openingSchema),
    mode: 'onTouched',
    defaultValues: { businessDate: today, amount: '' },
  });

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    client
      .request(OpeningBalancesController_current_v1, { signal: controller.signal })
      .then((balance) => {
        if (!live) return;
        setCurrent({ kind: 'found', balance });
        // A correction keeps the day unless it is moved.
        if (!form.formState.isDirty) form.setValue('businessDate', balance.businessDate);
      })
      .catch((error: unknown) => {
        if (!live || (error instanceof DOMException && error.name === 'AbortError')) return;
        // A 404 is the answer "none recorded yet", not a failure to answer.
        setCurrent(isNotFound(error) ? { kind: 'none' } : { kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads, form]);

  const pending = form.formState.isSubmitting;
  const correcting = current.kind === 'found' ? current.balance : null;

  const send = async (values: OpeningValues, correctsOpeningBalanceId: string | null) => {
    setFormError(null);
    setOffer(null);
    setRecorded(null);

    const body: OpeningBalancesController_record_v1Body = {
      businessDate: values.businessDate,
      amountPaise: paiseFromRupees(values.amount),
      ...(correctsOpeningBalanceId === null ? {} : { correctsOpeningBalanceId }),
    };
    const sending = attemptFor(attempt.current, JSON.stringify(body));
    attempt.current = sending;

    try {
      const balance = await client.request(OpeningBalancesController_record_v1, {
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
      });

      attempt.current = null;
      setRecorded(balance);
      setCurrent({ kind: 'found', balance });
      form.reset({ businessDate: balance.businessDate, amount: '' });
    } catch (error) {
      const code = error instanceof ApiError ? error.code : undefined;
      const currentId =
        error instanceof ApiError ? error.details?.['currentOpeningBalanceId'] : null;
      const correctable =
        code === 'REPORTING_OPENING_BALANCE_ALREADY_RECORDED' ||
        code === 'REPORTING_OPENING_BALANCE_SUPERSEDED';

      if (correctable && typeof currentId === 'string') {
        // What stands has changed under this screen; read it again so the
        // offer below sits beside the balance it will replace.
        reload();
        setOffer({ message: messageForFailure(error), currentId, values });

        return;
      }

      setFormError(messageForFailure(error));
    }
  };

  const handleSubmit = async (values: OpeningValues) => {
    if (pending) return;

    await send(values, correcting?.id ?? null);
  };

  return (
    <Screen
      title="Opening balance"
      description="The park’s cash in hand at the start of the first day of its daily sheet — one figure for the whole park, as its book keeps it. Every later day opens with the previous day’s closing."
      aside={
        current.kind === 'loading' ? undefined : (
          <ScreenFact label="Standing now">
            {current.kind === 'found' ? (
              <span className="numeric">{standing(current.balance)}</span>
            ) : current.kind === 'none' ? (
              'None recorded yet'
            ) : (
              messageForFailure(current.error)
            )}
          </ScreenFact>
        )
      }
      guidance={
        <>
          <ScreenNote label="Never edited">
            A correction is a new record naming the one it replaces, and may move the day as well as
            the amount. Both are kept.
          </ScreenNote>

          <div className="space-y-2 border-t border-border pt-4 text-sm">
            <Link
              to={paths.dailySheet}
              className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              The daily sheet
              <IconArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </>
      }
    >
      {recorded !== null && (
        <Alert role="status" className="mb-6">
          <IconCircleCheck />
          <AlertDescription className="numeric">
            The park’s sheet now starts from {standing(recorded)}.
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form
          className="space-y-6"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <DayField
            control={form.control}
            name="businessDate"
            label="First day of the sheet"
            maxDay={today}
            idPrefix="opening-balance"
            description="The day this cash was in hand at the start of. Not later than today."
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Cash in hand, in rupees
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11 font-mono numeric`}
                      required
                      inputMode="decimal"
                      placeholder="270121.85"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Counted across the park at the start of that day. Zero is a real answer.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {offer !== null && (
            <Alert>
              <IconAlertTriangle />
              <AlertDescription className="space-y-2">
                <p>{offer.message}</p>
                <p className="numeric">
                  {correcting === null || correcting.id !== offer.currentId
                    ? 'An opening balance already stands for the park.'
                    : `Standing now: ${standing(correcting)}.`}{' '}
                  Correcting it records {formatPaise(paiseFromRupees(offer.values.amount))} at the
                  start of {formatBusinessDate(offer.values.businessDate)} in its place.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="mt-8">
            {offer === null ? (
              <Button type="submit" disabled={pending || current.kind === 'loading'}>
                {pending ? 'Saving…' : correcting === null ? 'Record' : 'Record correction'}
              </Button>
            ) : (
              <Button
                type="button"
                disabled={pending}
                onClick={() => void form.handleSubmit(() => send(offer.values, offer.currentId))()}
              >
                {pending ? 'Saving…' : 'Correct it'}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </Screen>
  );
}
